// Admin dialog for the nightly sync from Teams. Written for a customer admin
// who has never seen the repository: what the sync is, what it does and
// never does, how to switch it on, and step-by-step instructions their IT
// can follow (or copy into a ticket), with the command line pre-filled.

import { Fragment, useEffect, useMemo, useState } from 'react';
import { X, RefreshCw, KeyRound, Copy, Check, AlertTriangle, Pencil, Trash2, Loader2, Download, ClipboardList, Info } from 'lucide-react';
import { api, type AutoSyncStatus, type AutoSyncMode, type AutoSyncRunSummary, type ImportProposal } from '../api/client';
import { SYNC_NEVER, SYNC_OUTCOMES, syncArguments, syncInstructionsText, syncSetupSteps, SECRET_ENV, type StepBlock } from './autoSyncGuide';

interface AutoSyncDialogProps {
  accentColor: string;
  appName?: string;
  onReview: (proposal: ImportProposal) => void;
  /** Fired after something that may have changed the call flows (dismiss, secret, mode). */
  onChanged?: () => Promise<unknown> | void;
  onClose: () => void;
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

async function copyText(value: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(value); return true; } catch { return false; }
}

/** Highlights <application-id>-style placeholders so IT can see what to replace. */
function CommandText({ value }: { value: string }) {
  return (
    <>
      {value.split(/(<[^<>]+>)/g).map((part, i) => (
        /^<[^<>]+>$/.test(part)
          ? <span key={i} className="text-amber-800 bg-amber-100/70 rounded-sm px-0.5">{part}</span>
          : <span key={i}>{part}</span>
      ))}
    </>
  );
}

function CopyField({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="flex items-start gap-1.5">
      {/* break-words, not break-all: long flags stay whole instead of splitting as "-Ten / antId". */}
      <code className="flex-1 min-w-0 whitespace-pre-wrap break-words text-[11px] leading-[1.6] font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-gray-800">
        <CommandText value={value} />
      </code>
      <button type="button" onClick={async () => { if (await copyText(value)) { setDone(true); setTimeout(() => setDone(false), 1500); } }}
        className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 shrink-0" title="Copy" aria-label="Copy to clipboard">
        {done ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-gray-600" />}
      </button>
    </div>
  );
}

/** One piece of a setup step; each kind gets the layout its content needs. */
function Block({ block, accentColor }: { block: StepBlock; accentColor: string }) {
  switch (block.kind) {
    case 'p':
      return <p className="text-xs text-gray-700 leading-relaxed">{block.text}</p>;
    case 'cmd':
      return <CopyField value={block.command} />;
    case 'link':
      return (
        <div className="space-y-1">
          <a href={block.href} download className="inline-flex items-center gap-1 text-xs font-medium underline underline-offset-2" style={{ color: accentColor }}>
            <Download className="w-3 h-3" />{block.text}
          </a>
          <CopyField value={block.href} />
        </div>
      );
    case 'option':
      return (
        <div className="rounded border border-gray-200 bg-gray-50/70 px-2.5 py-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-gray-900">{block.label}</span>
            {block.badge && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">{block.badge}</span>}
          </div>
          <p className="mt-1 text-xs text-gray-700 leading-relaxed">{block.text}</p>
          {block.items && (
            <ol className="mt-1.5 pl-4 list-decimal marker:text-gray-400 space-y-1 text-xs text-gray-700">
              {block.items.map((item, i) => <li key={i} className="leading-relaxed pl-0.5">{item}</li>)}
            </ol>
          )}
          {block.commands?.map(c => <div key={c} className="mt-1.5"><CopyField value={c} /></div>)}
        </div>
      );
    case 'rows':
      return (
        <div>
          {block.label && <div className="text-xs text-gray-700 mb-1.5">{block.label}</div>}
          <dl className="grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-1 text-xs">
            {block.items.map(([key, meaning]) => (
              <Fragment key={key}>
                <dt className="font-mono text-[11px] text-gray-900 bg-gray-100 border border-gray-200 rounded px-1.5 h-fit text-center">{key}</dt>
                <dd className="text-gray-700 leading-relaxed">{meaning}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      );
  }
}

function Section({ title, step, children, actions }: { title: string; step?: number; children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-2">
          {step !== undefined && <span className="w-5 h-5 rounded-full bg-gray-900 text-white text-[11px] flex items-center justify-center">{step}</span>}
          {title}
        </h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

const LEGEND: Array<[string, string]> = [
  ['Applied / Would apply', 'phone menus written (dry run: would have been written)'],
  ['Unchanged', 'identical to what Quick Dial already has'],
  ['Held', 'needs your review below before anything is written'],
  ['Stale', 'no longer in Teams; kept in Quick Dial, not deleted'],
  ['Missing audio', 'recordings the task still has to upload'],
];

function Summary({ s }: { s: AutoSyncRunSummary }) {
  const pill = (label: string, n: number, tone: string, hint: string) => (
    <span title={hint} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${tone}`}>{label} <b>{n}</b></span>
  );
  const dry = s.effectiveMode !== 'apply';
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-700">{dry ? 'dry run' : 'applied'}</span>
        {pill(dry ? 'Would apply' : 'Applied', dry ? s.wouldApply.length : s.applied.length, 'bg-green-50 text-green-800', LEGEND[0][1])}
        {pill('Unchanged', s.unchanged.length, 'bg-gray-50 text-gray-700', LEGEND[1][1])}
        {pill('Held', s.held.length, s.held.length ? 'bg-amber-50 text-amber-800' : 'bg-gray-50 text-gray-700', LEGEND[2][1])}
        {pill('Stale', s.stale.length, s.stale.length ? 'bg-red-50 text-red-800' : 'bg-gray-50 text-gray-700', LEGEND[3][1])}
        {pill('Missing audio', s.missingAudio.length, 'bg-gray-50 text-gray-700', LEGEND[4][1])}
      </div>
      {(dry ? s.wouldApply : s.applied).length > 0 && <div className="text-[11px] text-gray-600">{dry ? 'Would update' : 'Updated'}: {(dry ? s.wouldApply : s.applied).join(', ')}</div>}
      {s.stale.length > 0 && <div className="text-[11px] text-red-700">No longer in Teams (kept, not deleted): {s.stale.join(', ')}</div>}
      {s.warnings.map((w, i) => <div key={i} className="text-[11px] text-amber-800 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{w}</div>)}
    </div>
  );
}

export function AutoSyncDialog({ accentColor, appName, onReview, onChanged, onClose }: AutoSyncDialogProps) {
  const [status, setStatus] = useState<AutoSyncStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [freshSecret, setFreshSecret] = useState<string | null>(null);
  const [copiedGuide, setCopiedGuide] = useState(false);

  async function load() {
    try { setStatus(await api.getAutoSync()); setError(null); } catch (e: any) { setError(e?.message || 'Could not load the sync status'); }
  }
  useEffect(() => { load(); }, []);

  async function run<T>(key: string, fn: () => Promise<T>, after?: (v: T) => void) {
    setBusy(key);
    try { const v = await fn(); after?.(v); await load(); await onChanged?.(); }
    catch (e: any) { setError(e?.message || 'Request failed'); }
    finally { setBusy(null); }
  }

  const cfg = status?.config;
  const values = useMemo(() => status && cfg ? {
    apiOrigin: status.ingestBaseUrl, tenantId: cfg.tenantId,
    scriptUrl: `${window.location.origin}/export-teams-callflows.ps1`,
    registerScriptUrl: `${window.location.origin}/register-quickdial-sync-app.ps1`, appName,
  } : null, [status, cfg, appName]);
  const steps = useMemo(() => (values ? syncSetupSteps(values) : []), [values]);
  const setupOpen = !!cfg && (!cfg.hasSecret || cfg.runCount === 0);

  const stateLabel = !cfg ? '' : !cfg.enabled ? 'Off' : cfg.mode === 'apply' ? 'On · applying changes' : 'On · dry run';

  return (
    <div className="fixed inset-0 bg-black/40 z-40 overflow-y-auto" onClick={onClose}>
      <div className="max-w-3xl mx-auto my-8 bg-white rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" />Nightly sync from Teams</h2>
          <div className="flex items-center gap-3">
            {cfg && <span className={`text-[11px] px-2 py-0.5 rounded-full ${cfg.enabled ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{stateLabel}</span>}
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-5 space-y-6 text-sm text-gray-700">
          {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">{error}</div>}
          {!status && !error && <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>}

          {status && cfg && values && (
            <>
              {/* ---- How it works ---- */}
              <details open className="group">
                <summary className="cursor-pointer text-sm font-semibold text-gray-900 inline-flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden">
                  <Info className="w-4 h-4 text-gray-500" />How it works
                  <span className="text-[11px] font-normal text-gray-400 group-open:hidden">show</span>
                </summary>
                <div className="mt-2 space-y-3 text-xs text-gray-700 leading-relaxed">
                  <p>
                    Microsoft Teams offers no way for a service outside your organisation to read your auto attendants and call queues. That is why the sync
                    runs <em>inside your own Microsoft 365 organisation</em>: a small scheduled task on one of your machines (or an Azure Automation runbook)
                    signs in to Teams once a night with Microsoft's own PowerShell module, exports the phone-menu configuration with read-only commands, and
                    sends it to Quick Dial together with a secret you generate here. Quick Dial then updates the phone menus exactly as a manual import would,
                    with the same checks and the same audit trail.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-gray-200 rounded">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr><th className="text-left font-medium px-2 py-1.5">Situation</th><th className="text-left font-medium px-2 py-1.5">What Quick Dial does</th></tr>
                      </thead>
                      <tbody>
                        {SYNC_OUTCOMES.map(([a, b]) => (
                          <tr key={a} className="border-t border-gray-100"><td className="px-2 py-1.5 align-top text-gray-700">{a}</td><td className="px-2 py-1.5 align-top text-gray-900">{b}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 mb-1">The sync never</div>
                    <ul className="list-disc pl-5 space-y-0.5">{SYNC_NEVER.map(n => <li key={n}>{n}</li>)}</ul>
                  </div>
                  <p>
                    Switching it on is your consent to receive your organisation's voice configuration on a schedule. It is recorded in the audit log with your
                    name and can be switched off here at any time. See also the <a className="underline" href="/privacy" target="_blank" rel="noopener noreferrer">privacy statement</a>.
                  </p>
                </div>
              </details>

              {/* ---- 1. Turn it on ---- */}
              <Section step={1} title="Switch it on and choose the mode">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={cfg.enabled} disabled={busy !== null}
                      onChange={e => run('enable', () => api.putAutoSync({ enabled: e.target.checked }))} />
                    <span className="font-medium text-gray-900">Enable nightly sync</span>
                  </label>
                  <div className="inline-flex border border-gray-200 rounded p-0.5 bg-gray-50">
                    {(['dryRun', 'apply'] as AutoSyncMode[]).map(m => (
                      <button key={m} type="button" disabled={busy !== null}
                        onClick={() => run('mode', () => api.putAutoSync({ mode: m }))}
                        className={`px-2.5 py-1 text-xs rounded ${cfg.mode === m ? 'bg-white shadow-sm font-medium' : 'text-gray-600'}`}
                        style={cfg.mode === m ? { color: accentColor } : undefined}>
                        {m === 'dryRun' ? 'Dry run' : 'Apply'}
                      </button>
                    ))}
                  </div>
                  {cfg.consentedAt && <span className="text-[11px] text-gray-500">Enabled by {cfg.consentedBy} on {fmt(cfg.consentedAt)}</span>}
                </div>
                <ul className="text-[11px] text-gray-600 space-y-0.5 list-disc pl-5">
                  <li><b>Dry run</b> shows in "Last run" what would change, but writes nothing to the phone menus. Proposals that need review are still listed below.</li>
                  <li><b>Apply</b> writes the clean changes every night. The very first run of your organisation is always a dry run, whatever you choose.</li>
                  <li>Recommended: a few nights in dry run, read the summaries, then switch to Apply. Manual import keeps working alongside the sync.</li>
                </ul>
              </Section>

              {/* ---- 2. Secret ---- */}
              <Section step={2} title="Create the sync secret">
                <p className="text-[11px] text-gray-600">The scheduled task proves it is yours with this secret. Quick Dial stores only a fingerprint of it, so it can be shown once. Creating a new one stops the old one immediately.</p>
                {freshSecret ? (
                  <div className="space-y-1.5">
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">Copy this secret now; it will not be shown again. Your IT stores it as the machine-wide environment variable <code>{SECRET_ENV}</code> on the machine that runs the task (step 3.5).</div>
                    <CopyField value={freshSecret} />
                  </div>
                ) : (
                  <div className="text-xs text-gray-600">
                    {cfg.hasSecret ? <>A secret ending in <code className="font-mono">{cfg.secretHint}</code> exists, created {fmt(cfg.secretCreatedAt)}.</> : 'No secret yet.'}
                  </div>
                )}
                <button type="button" disabled={busy !== null}
                  onClick={() => run('secret', () => api.rotateAutoSyncSecret(), r => setFreshSecret(r.secret))}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">
                  <KeyRound className="w-3.5 h-3.5" />{cfg.hasSecret ? 'Create a new secret' : 'Generate secret'}
                </button>
              </Section>

              {/* ---- 3. Scheduled task ---- */}
              <Section step={3} title="Set up the scheduled task (for your IT)" actions={
                <div className="flex items-center gap-1.5">
                  <a href="/export-teams-callflows.ps1" download className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-700">
                    <Download className="w-3.5 h-3.5" />Download the script
                  </a>
                  <button type="button" onClick={async () => { if (await copyText(syncInstructionsText(values))) { setCopiedGuide(true); setTimeout(() => setCopiedGuide(false), 1500); } }}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-700">
                    {copiedGuide ? <Check className="w-3.5 h-3.5 text-green-600" /> : <ClipboardList className="w-3.5 h-3.5" />}{copiedGuide ? 'Copied' : 'Copy instructions for IT'}
                  </button>
                </div>
              }>
                <p className="text-[11px] text-gray-600">Everything your IT needs is below, with your organisation's values already filled in. "Copy instructions for IT" puts the same steps in your clipboard as plain text for a ticket or e-mail.</p>
                <div className="grid sm:grid-cols-2 gap-2 text-[11px] text-gray-600">
                  <div>Quick Dial API (the <code>-PostTo</code> value)<CopyField value={values.apiOrigin} /></div>
                  <div>Your tenant ID<CopyField value={values.tenantId} /></div>
                </div>
                <details open={setupOpen} className="group border border-gray-200 rounded">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-800 list-none [&::-webkit-details-marker]:hidden flex items-center justify-between">
                    Step-by-step instructions
                    <span className="text-[11px] font-normal text-gray-400"><span className="group-open:hidden">show</span><span className="hidden group-open:inline">hide</span></span>
                  </summary>
                  <ol className="border-t border-gray-200 divide-y divide-gray-200">
                    {steps.map((s, i) => (
                      <li key={s.title} className="px-3 py-3">
                        <div className="flex items-baseline gap-2">
                          <span className="shrink-0 text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded bg-gray-900 text-white">3.{i + 1}</span>
                          <h4 className="text-xs font-semibold text-gray-900">{s.title}</h4>
                        </div>
                        <div className="mt-2 ml-2 pl-3 border-l-2 border-gray-100 space-y-2">
                          {s.blocks.map((b, j) => <Block key={j} block={b} accentColor={accentColor} />)}
                        </div>
                      </li>
                    ))}
                  </ol>
                </details>
                <div>
                  <div className="text-[11px] text-gray-600 mb-1">Arguments for the task's "Start a program" action (program: <code>pwsh</code>)</div>
                  <CopyField value={syncArguments(values)} />
                </div>
              </Section>

              {/* ---- Last run ---- */}
              <Section title="Last run">
                {cfg.lastRunSummary ? (
                  <div className="border border-gray-200 rounded p-3 space-y-1.5">
                    <div className="text-[11px] text-gray-500">{fmt(cfg.lastRunAt)} · {cfg.runCount} run{cfg.runCount === 1 ? '' : 's'} so far{cfg.lastRunSummary.exportedAt && <> · export taken {fmt(cfg.lastRunSummary.exportedAt)}</>}</div>
                    <Summary s={cfg.lastRunSummary} />
                  </div>
                ) : <div className="text-xs text-gray-500">No run received yet. Once the scheduled task has run, its result appears here.</div>}
                <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500">
                  {LEGEND.map(([k, v]) => (
                    <div key={k} className="flex gap-1.5">
                      <dt className="font-medium text-gray-700 shrink-0">{k}</dt>
                      <dd className="min-w-0">{v}</dd>
                    </div>
                  ))}
                </dl>
                {status.runs.length > 1 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-600">Earlier runs ({status.runs.length - 1})</summary>
                    <ul className="mt-2 space-y-2">
                      {status.runs.slice(1).map(r => (
                        <li key={r.runId} className="border border-gray-100 rounded p-2">
                          <div className="text-[11px] text-gray-500 mb-1">{fmt(r.startedAt)}</div>
                          <Summary s={r.summary} />
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </Section>

              {/* ---- Held ---- */}
              <Section title="Held for your review" actions={status.pending.length > 0 ? <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">{status.pending.length}</span> : undefined}>
                {status.pending.length === 0 ? (
                  <div className="text-xs text-gray-500">Nothing is waiting. A proposal lands here when part of the Teams configuration cannot be represented or fails a check; you then open it in the editor and decide.</div>
                ) : (
                  <ul className="space-y-2">
                    {status.pending.map(p => (
                      <li key={p.id} className="border border-amber-200 bg-amber-50/40 rounded p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate">{p.proposal.locationName} <span className="text-[11px] text-gray-500 font-normal">({p.locationId}{p.proposal.existing ? '' : ', new'})</span></div>
                            <div className="text-[11px] text-gray-500">Held {fmt(p.receivedAt)}</div>
                            <ul className="mt-1 text-xs text-amber-900 list-disc pl-4">
                              {(p.proposal.lossyReasons.length ? p.proposal.lossyReasons : p.proposal.warnings).map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button type="button" onClick={() => onReview(p.proposal)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-white rounded" style={{ backgroundColor: accentColor }}>
                              <Pencil className="w-3 h-3" />Review &amp; save
                            </button>
                            <button type="button" disabled={busy !== null} onClick={() => run(`dismiss-${p.locationId}`, () => api.dismissAutoSyncPending(p.locationId))}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white text-gray-700" title="Dismiss until the next run holds it again">
                              <Trash2 className="w-3 h-3" />Dismiss
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
